/**
 * Verlauf-Checkliste: bestehende Vorlage plus gespeicherte Punkte nur dieser Akte.
 * Keine neue juristische Liste.
 */

export type ChecklistTemplatePoint = {
  id: string;
  label: string;
  required: boolean;
  order: number;
};

export type StoredChecklistPoint = {
  id?: string | null;
  akte_id?: string | null;
  item_id?: string | null;
  label?: string | null;
  required?: boolean | null;
  sort_order?: number | null;
  status?: string | null;
  /** `removed` blendet den Punkt nur in dieser Akte aus. Die Vorlage bleibt. */
  note?: string | null;
};

export type ChecklistViewPoint = {
  itemId: string;
  label: string;
  required: boolean;
  order: number;
  status: 'open' | 'done' | 'waived';
  recordId: string;
  custom: boolean;
  favorite: boolean;
};

/** Stand nur, wenn Punkte da sind. Leer heißt: Zeile weglassen. */
export function checklistStand(points: readonly { status?: string | null }[]): string {
  if (points.length === 0) return '';
  const done = points.filter((point) => point.status === 'done').length;
  return `${done} von ${points.length}`;
}

/** Marker im bestehenden Notizfeld. Kein neues Speicherformat. */
export const CHECKLIST_HIDDEN_NOTE = 'removed';
export const CHECKLIST_FAVORITE_NOTE = 'favorite';

export function checklistPointHidden(row?: { note?: string | null } | null): boolean {
  return (row?.note || '').trim() === CHECKLIST_HIDDEN_NOTE;
}

export function checklistPointFavorite(row?: { note?: string | null } | null): boolean {
  return (row?.note || '').trim() === CHECKLIST_FAVORITE_NOTE;
}

export function planCustomChecklistPoint(
  name?: string | null,
): { ok: true; label: string } | { ok: false; error: string } {
  const label = name?.trim() ?? '';
  if (!label) return { ok: false, error: 'Ohne Namen wird der Punkt nicht gespeichert.' };
  return { ok: true, label };
}

/** Nur im laufenden Besuch. Nach Reload ist der Stapel leer. */
export const CHECKLIST_UNDO_LIMIT = 5;

export type ChecklistUndoStep =
  | {
      kind: 'delete';
      itemId: string;
      label: string;
      favorite: boolean;
      custom: boolean;
      required: boolean;
      order: number;
      status: ChecklistViewPoint['status'];
    }
  | { kind: 'rename'; itemId: string; previousLabel: string }
  | { kind: 'favorite'; itemId: string; previousFavorite: boolean }
  | { kind: 'add'; itemId: string };

export function pushChecklistUndo(
  stack: readonly ChecklistUndoStep[],
  step: ChecklistUndoStep,
  limit = CHECKLIST_UNDO_LIMIT,
): ChecklistUndoStep[] {
  const next = [...stack, step];
  return next.length > limit ? next.slice(next.length - limit) : next;
}

/** Ein Schritt zurück, nur an den Zeilen dieser Akte. */
export function undoChecklistRows(
  stored: readonly StoredChecklistPoint[],
  akteId: string,
  step: ChecklistUndoStep,
): StoredChecklistPoint[] {
  const id = akteId.trim();
  const rows = stored.map((row) => ({ ...row }));
  const ownIndex = (itemId: string) =>
    rows.findIndex((row) => (row.akte_id || '').trim() === id && (row.item_id || '').trim() === itemId);
  if (step.kind === 'delete') {
    const note = step.favorite ? CHECKLIST_FAVORITE_NOTE : '';
    const idx = ownIndex(step.itemId);
    if (step.custom) {
      if (idx < 0) {
        rows.push({
          id: `undo-${step.itemId}`,
          akte_id: id,
          item_id: step.itemId,
          label: step.label,
          required: step.required,
          sort_order: step.order,
          status: step.status,
          note,
        });
      } else {
        rows[idx] = { ...rows[idx], label: step.label, note, status: step.status };
      }
      return rows;
    }
    if (idx >= 0) rows[idx] = { ...rows[idx], note };
    return rows;
  }
  if (step.kind === 'rename') {
    const idx = ownIndex(step.itemId);
    if (idx >= 0) rows[idx] = { ...rows[idx], label: step.previousLabel };
    return rows;
  }
  if (step.kind === 'favorite') {
    const idx = ownIndex(step.itemId);
    if (idx >= 0) rows[idx] = { ...rows[idx], note: step.previousFavorite ? CHECKLIST_FAVORITE_NOTE : '' };
    return rows;
  }
  return rows.filter(
    (row) => !((row.akte_id || '').trim() === id && (row.item_id || '').trim() === step.itemId),
  );
}

function asStatus(raw?: string | null): ChecklistViewPoint['status'] {
  if (raw === 'done' || raw === 'waived') return raw;
  return 'open';
}

/** Vorlage dieser Akte plus eigene Punkte. Zeilen einer anderen Akte fallen weg. */
export function checklistForAkte(
  akteId: string,
  templateItems: readonly ChecklistTemplatePoint[],
  stored: readonly StoredChecklistPoint[],
): ChecklistViewPoint[] {
  const id = akteId.trim();
  if (!id) return [];
  const own = stored.filter((row) => (row.akte_id || '').trim() === id);
  const byItem = new Map<string, StoredChecklistPoint>();
  for (const row of own) {
    const itemId = row.item_id?.trim() ?? '';
    if (itemId) byItem.set(itemId, row);
  }
  const templateIds = new Set(templateItems.map((item) => item.id));
  const fromTemplate = [...templateItems]
    .sort((a, b) => a.order - b.order)
    .filter((item) => !checklistPointHidden(byItem.get(item.id)))
    .map((item) => {
      const hit = byItem.get(item.id);
      const storedLabel = hit?.label?.trim() || '';
      return {
        itemId: item.id,
        label: storedLabel || item.label,
        required: item.required,
        order: item.order,
        status: asStatus(hit?.status),
        recordId: hit?.id?.trim() || '',
        custom: false,
        favorite: checklistPointFavorite(hit),
      };
    });
  const custom = own
    .filter((row) => {
      const itemId = row.item_id?.trim() ?? '';
      return itemId.startsWith('custom_') && !templateIds.has(itemId) && !checklistPointHidden(row);
    })
    .map((row) => ({
      itemId: row.item_id?.trim() || '',
      label: row.label?.trim() || '',
      required: Boolean(row.required),
      order: typeof row.sort_order === 'number' ? row.sort_order : 1000,
      status: asStatus(row.status),
      recordId: row.id?.trim() || '',
      custom: true,
      favorite: checklistPointFavorite(row),
    }))
    .filter((row) => row.itemId && row.label)
    .sort((a, b) => a.order - b.order);
  return [...fromTemplate, ...custom];
}
